import { useCallback, useMemo, useState } from 'react';
import { DuplicateGroupItem, Track } from '../../types/Track';
import { refreshRecycleBinBadge, reconcileSeratoCrates } from './duplicateIpc';
import { judgeGroup, Verdict } from './duplicateVerdict';
import { DuplicateCrateIndex } from './useDuplicateCrateIndex';
import { DuplicateGroupMutations } from './useDuplicateGroupMutations';
import {
  DuplicateResolverCore,
  groupBucketKey,
  trackBucketKey,
} from './useDuplicateResolver';

const APPLY_TIMEOUT_MS = 15000;

export type PlanStage = 'overview' | 'review' | 'done';

export type PlanEntry = {
  group: DuplicateGroupItem;
  verdict: Verdict;
};

export type PlanTotals = {
  sets: number;
  extraFiles: number;
  clearSets: number;
  clearFiles: number;
  reviewSets: number;
};

export type DuplicatePlan = ReturnType<typeof useDuplicatePlan>;

/**
 * Turns the raw duplicate groups into a reviewable plan: what to keep in each
 * set, how sure we are, and how much is left to decide. The page renders this
 * as three stages rather than a browsable list.
 */
export const useDuplicatePlan = (
  core: DuplicateResolverCore,
  mutations: DuplicateGroupMutations,
  crateIndex: DuplicateCrateIndex,
) => {
  const [stage, setStage] = useState<PlanStage>('overview');
  const [reviewIndex, setReviewIndex] = useState(0);
  const [keeperOverrides, setKeeperOverrides] = useState<Map<string, number>>(
    new Map(),
  );
  const [skippedGroupIds, setSkippedGroupIds] = useState<Set<string>>(
    new Set(),
  );
  const [removedFiles, setRemovedFiles] = useState(0);
  const [removedSets, setRemovedSets] = useState(0);
  const [reconciledCrates, setReconciledCrates] = useState(0);
  const [repointedEntries, setRepointedEntries] = useState(0);

  /** Every unresolved group paired with its tracks, indexed in one pass. */
  const entries = useMemo<PlanEntry[]>(() => {
    const allTracks = core.allTracksRef.current;
    if (!allTracks || core.duplicateGroupItems.length === 0) return [];

    const buckets = new Map<string, Track[]>();
    allTracks.forEach((track) => {
      const key = trackBucketKey(track);
      const bucket = buckets.get(key);
      if (bucket) bucket.push(track);
      else buckets.set(key, [track]);
    });

    return core.duplicateGroupItems
      .filter((group) => !group.isResolved && !group.isIgnored)
      .flatMap((group) => {
        const tracks = buckets.get(groupBucketKey(group)) ?? [];
        const verdict = judgeGroup(tracks, crateIndex.lookup);
        return verdict ? [{ group, verdict }] : [];
      });
  }, [core.duplicateGroupItems, core.allTracksRef, crateIndex.lookup]);

  const withOverride = useCallback(
    (entry: PlanEntry): PlanEntry => {
      const override = keeperOverrides.get(entry.group.id);
      if (override === undefined || override === entry.verdict.keeperId) {
        return entry;
      }
      return {
        group: entry.group,
        verdict: {
          ...entry.verdict,
          keeperId: override,
          removeIds: entry.verdict.facts
            .map((fact) => fact.track.id)
            .filter((id) => id !== override),
        },
      };
    },
    [keeperOverrides],
  );

  const clearEntries = useMemo(
    () => entries.filter((entry) => entry.verdict.confidence === 'clear'),
    [entries],
  );

  const reviewEntries = useMemo(
    () =>
      entries
        .filter((entry) => entry.verdict.confidence === 'review')
        .filter((entry) => !skippedGroupIds.has(entry.group.id))
        .map(withOverride),
    [entries, skippedGroupIds, withOverride],
  );

  const totals = useMemo<PlanTotals>(
    () => ({
      sets: entries.length,
      extraFiles: entries.reduce(
        (sum, entry) => sum + entry.verdict.removeIds.length,
        0,
      ),
      clearSets: clearEntries.length,
      clearFiles: clearEntries.reduce(
        (sum, entry) => sum + entry.verdict.removeIds.length,
        0,
      ),
      reviewSets: reviewEntries.length,
    }),
    [entries, clearEntries, reviewEntries],
  );

  const reconcileEntry = useCallback(
    async (entry: PlanEntry) => {
      if (!crateIndex.available) return;

      const keeper = entry.verdict.facts.find(
        (fact) => fact.track.id === entry.verdict.keeperId,
      )?.track;
      if (!keeper) return;

      const doomed = entry.verdict.facts
        .filter((fact) => entry.verdict.removeIds.includes(fact.track.id))
        .map((fact) => fact.track);
      if (doomed.length === 0) return;

      const repoint: Record<string, string> = {};
      doomed.forEach((track) => {
        repoint[track.filePath] = keeper.filePath;
      });

      const result = await reconcileSeratoCrates({
        remove: doomed.map((track) => track.filePath),
        repoint,
      });

      if (result.failures && result.failures.length > 0) {
        mutations.reportDeleteFailures(
          'Serato crate update failed',
          result.failures,
        );
      }

      if (typeof result.repointed === 'number') {
        setRepointedEntries((count) => count + result.repointed!);
      }
      if (Array.isArray(result.crates)) {
        setReconciledCrates(
          (count) =>
            count +
            result.crates!.filter(
              (crate) =>
                crate.removed > 0 || crate.repointed > 0 || crate.merged > 0,
            ).length,
        );
      }
    },
    [crateIndex.available, mutations],
  );

  /** Moves one set's rejected copies to the recycle bin. */
  const applyEntry = useCallback(
    async (entry: PlanEntry): Promise<number> => {
      const doomed = entry.verdict.facts
        .filter((fact) => entry.verdict.removeIds.includes(fact.track.id))
        .map((fact) => fact.track);
      if (doomed.length === 0) return 0;

      await reconcileEntry(entry);

      const { successCount, errors } = await mutations.deleteGroupTracks(
        entry.group.id,
        doomed,
        APPLY_TIMEOUT_MS,
      );

      if (errors.length === 0) {
        mutations.patchGroup(entry.group.id, { isResolved: true });
        core.persistGroupState(entry.group.id, true, false);
        setRemovedSets((count) => count + 1);
      } else {
        mutations.reportDeleteFailures(
          `Couldn't remove every copy of “${entry.group.name}”`,
          errors,
        );
      }

      setRemovedFiles((count) => count + successCount);
      return successCount;
    },
    [core, mutations, reconcileEntry],
  );

  const runApply = useCallback(
    async (batch: PlanEntry[], onDone: () => void) => {
      if (batch.length === 0 || core.deleteInProgressRef.current) return;

      core.deleteInProgressRef.current = true;
      mutations.beginDeleteProgress(
        batch.reduce((sum, entry) => sum + entry.verdict.removeIds.length, 0),
      );

      try {
        for (const entry of batch) {
          await applyEntry(entry);
        }
      } finally {
        mutations.endDeleteProgress();
        refreshRecycleBinBadge();
        core.deleteInProgressRef.current = false;
        onDone();
      }
    },
    [applyEntry, core, mutations],
  );

  const applyClearSets = useCallback(async () => {
    const batch = clearEntries.map(withOverride);
    await runApply(batch, () => {
      setStage(reviewEntries.length > 0 ? 'review' : 'done');
      setReviewIndex(0);
    });
  }, [clearEntries, reviewEntries.length, runApply, withOverride]);

  const currentEntry = reviewEntries[reviewIndex] ?? null;

  const chooseKeeper = useCallback((groupId: string, trackId: number) => {
    setKeeperOverrides((prev) => new Map(prev).set(groupId, trackId));
  }, []);

  const advance = useCallback(() => {
    setReviewIndex((index) => {
      const remaining = reviewEntries.length - 1;
      return index >= remaining ? Math.max(remaining - 1, 0) : index;
    });
  }, [reviewEntries.length]);

  const confirmCurrent = useCallback(async () => {
    if (!currentEntry) return;
    await runApply([currentEntry], advance);
  }, [advance, currentEntry, runApply]);

  const skipCurrent = useCallback(() => {
    if (!currentEntry) return;
    setSkippedGroupIds((prev) => new Set(prev).add(currentEntry.group.id));
  }, [currentEntry]);

  const keepAllInCurrent = useCallback(() => {
    if (!currentEntry) return;
    core.persistGroupState(currentEntry.group.id, false, true);
    mutations.patchGroup(currentEntry.group.id, { isIgnored: true });
  }, [core, currentEntry, mutations]);

  const finishReview = useCallback(() => setStage('done'), []);

  const startOver = useCallback(() => {
    setSkippedGroupIds(new Set());
    setKeeperOverrides(new Map());
    setRemovedFiles(0);
    setRemovedSets(0);
    setReconciledCrates(0);
    setRepointedEntries(0);
    setReviewIndex(0);
    setStage('overview');
  }, []);

  return {
    stage,
    setStage,
    totals,
    clearEntries,
    reviewEntries,
    currentEntry,
    reviewIndex,
    skippedCount: skippedGroupIds.size,
    removedFiles,
    removedSets,
    reconciledCrates,
    repointedEntries,
    crateIndexLoading: crateIndex.loading,
    crateIndexAvailable: crateIndex.available,
    applyClearSets,
    chooseKeeper,
    confirmCurrent,
    skipCurrent,
    keepAllInCurrent,
    finishReview,
    startOver,
  };
};
